// Shared issue-body parsing + client-side Definition-of-Ready meter.
//
// Both the per-issue page (`IssuePage`) and the legacy modal editor
// (`IssueEditor`) seed their form fields from a GitLab issue body that
// Loom renders with `## What you need / ## Business outcome / ##
// Acceptance criteria` sections. The parse is intentionally lenient
// about the acceptance-criteria list markers (numbered, bullets,
// checkboxes) because pre-existing issues use whatever GitLab markdown
// their author typed.
//
// `buildDorChecks` is the *client* readiness meter — fast local feedback
// only. The server-side rubric is authoritative and runs at save time.
//
// `diffDraftPatch` decides what a Save SENDS: only what the user changed.

import type { Priority } from "@loom/shared/dashboard";
import { editorViewOf, parseIssueBody } from "@loom/shared/issue-body";

import type { MessageKey } from "../i18n/index.js";

import type { IssuePatch } from "./useUpdateIssue";

export interface ParsedBody {
  readonly description: string;
  readonly outcome: string;
  readonly acceptanceCriteria: string[];
  /**
   * WHERE the Description came from — `## What you need` when the body has that
   * section, the text above the first heading otherwise.
   *
   * Optional so every existing `IssueDraftFields` literal still type-checks; the
   * server recomputes it from the CURRENT body before a Save writes, because the
   * body may have moved on since this parse.
   */
  readonly descriptionFrom?: "what_you_need" | "preamble";
}

/**
 * What the editor shows for an issue body.
 *
 * A one-line delegate to `@loom/shared/issue-body`, which is now the single
 * owner of an issue description: the same parser the server writes through, so
 * "what the editor shows" and "what a Save keeps" can no longer disagree. Two
 * things that closes, both of them bugs this file used to have:
 *
 *  - a REFINED ticket's story sentence has no heading of its own. This parser
 *    ignored everything above the first `## ` line as soon as any known section
 *    matched, so 12 of the 20 latest `dor:pass` issues opened with an empty
 *    Description and the user could not edit what was plainly on the ticket.
 *    The story is the Description now (`descriptionFrom: "preamble"`).
 *  - criteria written by the CHAT create flow carry a bold
 *    `**Acceptance criteria**` label instead of a heading. This parser found
 *    zero of them; the shared parser lifts the label block into rows.
 *
 * Machine markers are still never field content: the shared parser drops
 * HTML-comment-only lines, and drops them a second time AFTER the list marker
 * is stripped, because a body an earlier Save corrupted carries the marker WITH
 * a list prefix (`3. <!-- … -->`).
 */
export const parseBody = (raw: string | null): ParsedBody =>
  editorViewOf(parseIssueBody(raw));

/** The editable shape of an issue: the three parsed sections plus the two
 *  fields that live outside the body. */
export type IssueDraftFields = ParsedBody & {
  readonly title: string;
  readonly priority: Priority;
};

export const NEW_DRAFT_DEFAULTS: IssueDraftFields = {
  title: "",
  description: "",
  outcome: "",
  acceptanceCriteria: [""],
  priority: "normal",
};

/** Acceptance criteria as they are sent and compared: trimmed, blanks dropped. */
export const normalizeAc = (ac: ReadonlyArray<string>): string[] =>
  ac.map((s) => s.trim()).filter((s) => s.length > 0);

/** Same content, as far as a Save is concerned — the page's dirty flag. */
export const draftsEqual = (a: IssueDraftFields, b: IssueDraftFields): boolean =>
  a.title.trim() === b.title.trim() &&
  a.description === b.description &&
  a.outcome === b.outcome &&
  a.priority === b.priority &&
  JSON.stringify(normalizeAc(a.acceptanceCriteria)) ===
    JSON.stringify(normalizeAc(b.acceptanceCriteria));

/**
 * The PATCH a Save sends: exactly what the user changed against the last
 * loaded (or last saved) values, or `null` when there is nothing to send.
 *
 * Why this is not "send the whole draft": the server rebuilds the body from the
 * three editor sections whenever they arrive, and a refined ticket carries more
 * than those three — the story line, `## Definition of Done`, the plan, the
 * server blocks. The server now carries all of that across a rebuild, but a Save
 * that fixed a typo in the title still has no business rewriting the body at
 * all: GitLab keeps no description history, so every body write is one nobody
 * can undo.
 *
 *  - `title` travels when its TRIMMED text changed, trimmed. A blank title is
 *    not sent: it has always meant "keep the old one" (the server would refuse
 *    it), and sending the old title back would only buy a DoR re-check.
 *  - `priority` travels when it changed.
 *  - the BODY TRIO — `description`, `outcome`, `acceptanceCriteria` — travels
 *    together when any one of the three changed, and never alone: the server
 *    fills a missing member with "", so a lone field would blank the other two.
 *    The comparison is `draftsEqual`'s, so the dirty flag and the patch agree.
 */
export const diffDraftPatch = (
  draft: IssueDraftFields,
  baseline: IssueDraftFields,
): IssuePatch | null => {
  const title = draft.title.trim();
  const titleChanged = title.length > 0 && title !== baseline.title.trim();
  const priorityChanged = draft.priority !== baseline.priority;
  const acceptanceCriteria = normalizeAc(draft.acceptanceCriteria);
  const bodyChanged =
    draft.description !== baseline.description ||
    draft.outcome !== baseline.outcome ||
    JSON.stringify(acceptanceCriteria) !==
      JSON.stringify(normalizeAc(baseline.acceptanceCriteria));
  if (!titleChanged && !priorityChanged && !bodyChanged) return null;
  return {
    ...(titleChanged ? { title } : {}),
    ...(bodyChanged
      ? {
          description: draft.description,
          outcome: draft.outcome,
          acceptanceCriteria,
        }
      : {}),
    ...(priorityChanged ? { priority: draft.priority } : {}),
  };
};

/**
 * Dirty-guard decision for a document refetch. When the server payload
 * changes (e.g. the embedded agent wrote the issue), the page either
 * adopts the new truth or stashes it behind a "review" banner:
 *   - no existing draft (initial load) → adopt.
 *   - existing draft, clean (no unsaved edits) → adopt silently.
 *   - existing draft, dirty (unsaved edits) → stash; never clobber.
 */
export type ReseedAction = "adopt" | "stash";

export const reseedAction = (
  hasExistingDraft: boolean,
  dirty: boolean,
): ReseedAction => (!hasExistingDraft || !dirty ? "adopt" : "stash");

export interface DorCheck {
  readonly ok: boolean;
  readonly labelKey: MessageKey;
}

/**
 * The design's four readiness checks. Kept identical between the page and
 * the modal so the meter reads the same wherever it renders. The label is
 * carried as a catalog key; the rendering surface translates it.
 */
export const buildDorChecks = (
  title: string,
  description: string,
  outcome: string,
  ac: ReadonlyArray<string>,
): DorCheck[] => [
  { ok: title.trim().length > 6, labelKey: "dashboard.dorCheck.title" },
  {
    ok: description.trim().length > 24,
    labelKey: "dashboard.dorCheck.description",
  },
  { ok: outcome.trim().length > 6, labelKey: "dashboard.dorCheck.outcome" },
  {
    ok: ac.filter((a) => a.trim().length > 8).length >= 1,
    labelKey: "dashboard.dorCheck.acceptance",
  },
];
